// Free beside Premium.
//
// The two cards are white on both themes rather than the app's glass, because a comparison wants a
// flat surface where the only thing that differs between the columns is the content.
//
// The price is never hardcoded here — it is read from Stripe, so editing it in the dashboard is
// enough. If Stripe can't be reached the card renders without a price rather than with a stale one;
// the Upgrade button still works, and checkout shows the real amount before anyone is charged.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  annualSaving,
  fetchEntitlement,
  fetchPrices,
  openBillingPortal,
  startCheckout,
  type Entitlement,
  type PlanId,
  type Price,
} from '../api/billing';
import { apiGet } from '../api/client';
import { Notice, PrimaryButton, Spinner } from '../components/ui';
import { IconLock, IconSpark } from '../components/Icons';
import { FREE_FEATURES, PREMIUM_FEATURES, segments, AI_FAIR_USE } from './pricing-tiers';
import freeArt from '../assets/pricing/tier-free.webp';
import premiumArt from '../assets/pricing/tier-premium.webp';

export function PricingScreen() {
  const navigate = useNavigate();
  const [allowance,setAllowance]=useState<{premium:boolean;remainingPercent:number;voiceCharsRemaining:number;freeRepliesRemaining:number|null;resetsAt:string;visionCredits:number}|null>(null);
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [prices, setPrices] = useState<Price[]>([]);
  const [plan, setPlan] = useState<PlanId>('monthly');
  const [busy, setBusy] = useState<'checkout' | 'portal' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    // Independent on purpose: an unreadable price should still leave the plan state correct, and a
    // failed entitlement lookup should not blank out the price.
    void fetchEntitlement()
      .then(setEntitlement)
      .catch(() => setEntitlement(null));
    void fetchPrices(controller.signal).then(setPrices);
    void apiGet<typeof allowance>('/api/ai/allowance').then(setAllowance).catch(()=>undefined);
    return () => controller.abort();
  }, []);

  const isPremium = entitlement?.isPremium ?? false;
  const viaApple = ['apple','appstore'].includes(entitlement?.source??'');
  const selected = prices.find((p) => p.plan === plan) ?? prices[0] ?? null;
  const saving = annualSaving(prices);

  async function upgrade() {
    setBusy('checkout');
    setError(null);
    try {
      window.location.href = await startCheckout(selected?.plan ?? 'monthly');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open checkout.');
      setBusy(null);
    }
  }

  async function manageBilling() {
    setBusy('portal');
    setError(null);
    try {
      window.location.href = await openBillingPortal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open your billing portal.');
      setBusy(null);
    }
  }

  return (
    <>
      <div className="ohead">
        <div className="obrand">
          <span className="nm" style={{ fontSize: 22 }}>Plans</span>
        </div>
        <div className="olock">
          <IconLock /> Private
        </div>
      </div>

      <div className="ojhead">
        <h1>
          Journaling itself is
          <br />
          <em className="oitalic">free, forever.</em>
        </h1>
        <p className="ojsub">
          Explore your dreams through Jungian, biological, and spiritual readings. Premium gives
          you room to keep exploring — readings and follow-up conversations within monthly fair use, Dream Visions,
          and your week read back to you.
        </p>
      </div>

      <p className="ojsub" style={{fontSize:12,marginBottom:20}}>{AI_FAIR_USE}</p>
      {allowance ? <p className="ojsub" style={{fontSize:13,marginBottom:20}}>
        {allowance.premium ? `${allowance.remainingPercent}% of your shared AI allowance remains · ${allowance.voiceCharsRemaining.toLocaleString()} cloud-speech characters left · ${allowance.visionCredits} image credits per month.` : `${allowance.freeRepliesRemaining} free AI replies remain this month.`}
        {' '}Shared AI renews {new Date(allowance.resetsAt).toLocaleDateString(undefined,{month:'short',day:'numeric',timeZone:'UTC'})} (UTC). Image credits follow your image allowance period.
      </p> : null}

      {/* Above the cards rather than inside the paid one: it keeps the two columns the same shape,
          so the feature lists still line up and can be read across. */}
      {prices.length > 1 && !isPremium ? (
        <PlanSwitch plan={plan} onChange={setPlan} saving={saving} />
      ) : null}

      {error ? (
        // No role here: a danger Notice is already role="alert", and nesting two makes screen
        // readers announce the same sentence twice.
        <div style={{ marginBottom: 16 }}>
          <Notice tone="danger">{error}</Notice>
        </div>
      ) : null}

      <div className="oprice-grid">
        <TierCard
          art={freeArt}
          badge="Free"
          name="Free"
          tagline="Everything you need to keep a private journal."
          amount="$0"
          period="always"
          // Mirrors Premium's trial line so both cards keep the same vertical rhythm.
          note="No card, ever."
          features={FREE_FEATURES}
          featuresLabel="Included"
          action={
            isPremium ? (
              <span className="oprice-btn is-quiet">Included with Premium</span>
            ) : (
              <span className="oprice-btn is-quiet">Your current plan</span>
            )
          }
        />

        <TierCard
          highlighted
          art={premiumArt}
          badge="Premium"
          name="Oreia Premium"
          tagline="For when the journal starts answering back."
          amount={selected?.display ?? null}
          period={selected?.interval ? `/ ${selected.interval}` : null}
          note={
            selected?.trialDays
              ? `First ${selected.trialDays} days free. Cancel before then and you pay nothing.`
              : 'Cancel any time, from here.'
          }
          features={PREMIUM_FEATURES}
          featuresLabel="Everything in Free, plus"
          action={
            entitlement === null ? (
              <Spinner label="Checking your plan…" />
            ) : isPremium ? (
              viaApple ? (
                // Only the App Store can change an App Store subscription; offering a Stripe portal
                // here would send someone to a page that cannot cancel what they actually bought.
                <span className="oprice-btn is-quiet">
                  Active — manage it on your iPhone
                </span>
              ) : (
                <button
                  className="oprice-btn is-quiet"
                  onClick={() => void manageBilling()}
                  disabled={busy === 'portal'}
                >
                  {busy === 'portal' ? 'Opening…' : 'Manage billing'}
                </button>
              )
            ) : (
              <PrimaryButton onClick={() => void upgrade()} loading={busy === 'checkout'}>
                <IconSpark /> Upgrade
              </PrimaryButton>
            )
          }
        />
      </div>

      <p className="oprice-foot">
        Cancel any time. Your entries stay yours and stay encrypted either way.{' '}
        <button className="olink-plain" onClick={() => navigate('/settings')}>
          Back to settings
        </button>
      </p>

      <div style={{ height: 60 }} />
    </>
  );
}

/** Monthly or yearly, as a two-option segmented control. */
function PlanSwitch({
  plan,
  onChange,
  saving,
}: {
  plan: PlanId;
  onChange: (plan: PlanId) => void;
  saving: string | null;
}) {
  return (
    <div className="oprice-switch" role="radiogroup" aria-label="Billing period">
      {(['monthly', 'annual'] as const).map((id) => (
        <button
          key={id}
          type="button"
          role="radio"
          aria-checked={plan === id}
          className={plan === id ? 'is-on' : undefined}
          onClick={() => onChange(id)}
        >
          {id === 'monthly' ? 'Monthly' : 'Yearly'}
          {id === 'annual' && saving ? <em>{saving}</em> : null}
        </button>
      ))}
    </div>
  );
}

function TierCard({
  art,
  badge,
  name,
  tagline,
  amount,
  period,
  note,
  features,
  featuresLabel,
  action,
  highlighted,
}: {
  art: string;
  badge: string;
  name: string;
  tagline: string;
  amount: string | null;
  period: string | null;
  note?: string | null;
  features: readonly string[];
  featuresLabel: string;
  action: React.ReactNode;
  highlighted?: boolean;
}) {
  return (
    <section className={`oprice-card${highlighted ? ' is-premium' : ''}`} aria-label={name}>
      <div className="oprice-art">
        <span className={`oprice-badge${highlighted ? '' : ' is-plain'}`}>{badge}</span>
        <img src={art} alt="" aria-hidden />
      </div>
      <div className="oprice-body">
        <h2 className="oprice-name">{name}</h2>
        <p className="oprice-tagline">{tagline}</p>

        <div className="oprice-amount">
          {amount ? (
            <>
              <span className="n">{amount}</span>
              {period ? <span className="p">{period}</span> : null}
            </>
          ) : (
            // No price rather than a guessed one. Checkout still shows the real amount.
            <span className="p">See the price at checkout</span>
          )}
        </div>

        {note ? <p className="oprice-note">{note}</p> : null}

        <div className="oprice-action">{action}</div>

        <div className="oprice-flabel">{featuresLabel}</div>
        <ul className="oprice-list">
          {features.map((line) => (
            <li key={line}>
              <Tick />
              <span>
                {segments(line).map((seg, i) =>
                  seg.strong ? <b key={i}>{seg.text}</b> : <span key={i}>{seg.text}</span>,
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Tick() {
  return (
    <svg className="oprice-tick" viewBox="0 0 16 16" aria-hidden>
      <circle cx="8" cy="8" r="8" />
      <path d="M4.6 8.3 6.9 10.6 11.4 5.7" />
    </svg>
  );
}
